<template>
  <MinimalLayout :title="t('join.title')">
    <template v-if="step === 'form'">
      <v-form
        ref="form"
        :disabled="loading"
        validate-on="invalid-input lazy"
        @submit.prevent="submit"
      >
        <v-text-field
          v-model.trim="email"
          :rules="[required, validEmail]"
          clearable
          :label="t('join.email')"
          class="mb-2"
          variant="outlined"
          autocomplete="email"
        ></v-text-field>

        <v-alert v-if="errorMessage" density="compact" class="mb-3" color="error" type="error">
          <span>{{ errorMessage }}</span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
        >
          {{ t("join.next") }}
        </v-btn>
      </v-form>
    </template>

    <ExpiredSentStatus
      v-else-if="step === 'sent'"
      :message="t('join.checkEmailForLink', { email })"
    />
  </MinimalLayout>
</template>

<script lang="ts" setup>
import ExpiredSentStatus from "@/components/ExpiredSentStatus.vue";
import MinimalLayout from "@/components/MinimalLayout.vue";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useFirebaseErrorMessage } from "@/utils/errors";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import { ref } from "vue";
import { useRoute } from "vue-router";
import type { VForm } from "vuetify/components";

const { t } = useI18n();
const { currentLanguage } = useLanguage();
const route = useRoute();
const { getFirebaseErrorMessage } = useFirebaseErrorMessage();
const { required, validEmail } = useRules();

const email = ref(typeof route.query.email === "string" ? route.query.email : "");
const loading = ref(false);
const form = ref<VForm>();
const errorMessage = ref<string>("");
const step = ref<"form" | "sent">("form");

const submit = async () => {
  errorMessage.value = "";
  const validateForm = await form.value?.validate();
  if (!validateForm?.valid) return;

  loading.value = true;
  try {
    console.log("Sending user invitation email");
    await call("sendTreezorUserInvitationEmail")({
      email: email.value,
      lang: currentLanguage.value.code,
    });
    step.value = "sent";
  } catch (cause) {
    const error = new Error("Error sending user invitation email", { cause });
    console.error(error);
    captureException(error);
    if (cause instanceof FirebaseError) {
      errorMessage.value = getFirebaseErrorMessage(cause);
    } else {
      errorMessage.value = t("join.error");
    }
  }
  loading.value = false;
};
</script>
